import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Drawer, Form, Input, Modal, Popconfirm, Table } from 'antd';
import { listSupervisors, createSupervisor, updateSupervisor, deleteSupervisor, assignResidences, listAssignedResidences } from '../../api/supervisors';
import { listResidences } from '../../api/residences';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard, { AntPagination } from '../../components/TableCard';
import { ViewIcon, EditIcon, DeleteIcon } from '../../components/UIKit';

const AssignIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

export default function Supervisors() {
  const { message, notification } = App.useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewRows, setViewRows] = useState([]);
  const [viewPage, setViewPage] = useState(1);
  const viewPageSize = 8;
  const [editing, setEditing] = useState(null);
  const [resOpts, setResOpts] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [form] = Form.useForm();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [supers, residences] = await Promise.all([listSupervisors({}), listResidences({})]);
      setRows(supers);
      setResOpts(residences.map(r => ({ key: String(r.id), title: r.nom })));
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les superviseurs.'), placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [notification]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onEdit = useCallback((record) => { setEditing(record); form.setFieldsValue({ nom: record.nom, prenom: record.prenom, email: record.email }); setOpen(true); }, [form]);
  const onDelete = useCallback(async (record) => {
    try { await deleteSupervisor(record.id); message.success('Superviseur supprimé.'); fetchAll(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Suppression échouée.'), placement: 'topRight' }); }
  }, [fetchAll, message, notification]);

  const onAssign = useCallback(async (record) => {
    setEditing(record);
    try { const current = await listAssignedResidences(record.id); setAssigned(current.map(r => String(r.id))); setAssignOpen(true); }
    catch { notification.error({ message: 'Impossible de charger les affectations.', placement: 'topRight' }); }
  }, [notification]);

  const onView = useCallback(async (record) => {
    setEditing(record); setViewPage(1); setViewOpen(true); setViewLoading(true);
    try { const current = await listAssignedResidences(record.id); setViewRows(current || []); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les résidences.'), placement: 'topRight' }); setViewRows([]); }
    finally { setViewLoading(false); }
  }, [notification]);

  const pagedViewRows = useMemo(() => {
    const start = (viewPage - 1) * viewPageSize;
    return (viewRows || []).slice(start, start + viewPageSize);
  }, [viewPage, viewRows]);

  const submitAssign = async () => {
    try { await assignResidences(editing.id, (assigned || []).map((k) => Number(k))); message.success('Affectations mises à jour.'); setAssignOpen(false); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Enregistrement échoué.'), placement: 'topRight' }); }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) { await updateSupervisor(editing.id, { nom: values.nom, prenom: values.prenom, email: values.email }); message.success('Superviseur modifié.'); }
      else { await createSupervisor(values); message.success('Superviseur créé.'); }
      setOpen(false); fetchAll();
    } catch (e) { if (e?.response || e?.message) notification.error({ message: getApiErrorMessage(e, 'Enregistrement échoué.'), placement: 'topRight' }); }
  };

  return (
    <div className="page">
      <TableCard
        label="Superviseurs"
        loading={loading}
        rows={rows}
      >
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Prénom</th>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id}>
                <td>{r.nom || '-'}</td>
                <td>{r.prenom || '-'}</td>
                <td>{r.email || '-'}</td>
                <td>
                  <div className="actions-cell">
                    <button className="action-btn" title="Voir" onClick={() => onView(r)}><ViewIcon /></button>
                    <button className="action-btn" title="Modifier" onClick={() => onEdit(r)}><EditIcon /></button>
                    <button className="action-btn" title="Affecter des résidences" onClick={() => onAssign(r)}><AssignIcon /></button>
                    <Popconfirm title="Supprimer ce superviseur ?" description={r.email} okText="Supprimer" cancelText="Annuler" okType="danger" onConfirm={() => onDelete(r)}>
                      <button className="action-btn danger" title="Supprimer"><DeleteIcon /></button>
                    </Popconfirm>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </TableCard>

      {/* Form Drawer */}
      <Drawer title={editing ? 'Modifier superviseur' : 'Nouveau superviseur'} placement="right" width={480} onClose={() => setOpen(false)} open={open} destroyOnClose extra={<div style={{ display: 'flex', gap: 8 }}><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={onSubmit}>{editing ? 'Enregistrer' : 'Créer'}</button></div>}>
        <Form form={form} layout="vertical">
          <Form.Item name="nom" label="Nom" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="prenom" label="Prénom" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item>
          {!editing && <Form.Item name="password" label="Mot de passe" rules={[{ required: true, min: 8 }]}><Input.Password /></Form.Item>}
        </Form>
      </Drawer>

      {/* View residences */}
      <Modal title={`Résidences affectées — ${editing?.email || ''}`} open={viewOpen} onCancel={() => { setViewOpen(false); setViewRows([]); }} footer={<button className="btn-outline" onClick={() => { setViewOpen(false); setViewRows([]); }}>Fermer</button>}>
        {viewLoading ? <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Chargement…</div> : (
          <>
            <div className="table-card"><div className="table-scroll">
              <table><thead><tr><th>Résidence</th></tr></thead>
                <tbody>{pagedViewRows.map((r) => <tr key={r.id}><td>{r.nom}</td></tr>)}</tbody>
              </table>
            </div></div>
            <div style={{ marginTop: 8 }}>
              <AntPagination current={viewPage} total={viewRows.length} pageSize={viewPageSize} onChange={setViewPage} />
            </div>
          </>
        )}
      </Modal>

      {/* Assign residences Modal */}
      <Modal title={`Affecter résidences — ${editing?.email || ''}`} open={assignOpen} onCancel={() => setAssignOpen(false)} onOk={submitAssign} okText="Enregistrer" width={580}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {['Disponibles', 'Affectées'].map((title, side) => {
            const isRight = side === 1;
            const items = isRight
              ? resOpts.filter(r => assigned.includes(r.key))
              : resOpts.filter(r => !assigned.includes(r.key));
            return (
              <div key={title} style={{ border: '0.5px solid rgba(0,0,0,0.1)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', background: '#f7f3ee', borderBottom: '0.5px solid rgba(0,0,0,0.08)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#888' }}>
                  {title} <span style={{ fontWeight: 700, color: '#333' }}>({items.length})</span>
                </div>
                <div style={{ maxHeight: 280, overflowY: 'auto', padding: '6px 0' }}>
                  {items.length === 0 && <div style={{ padding: '12px 14px', fontSize: 12, color: '#bbb' }}>Aucune résidence</div>}
                  {items.map(r => (
                    <label key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0ede8'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <input type="checkbox" checked={isRight}
                        onChange={() => setAssigned(prev => isRight ? prev.filter(k => k !== r.key) : [...prev, r.key])}
                        style={{ accentColor: '#3b6d11', width: 14, height: 14 }}
                      />
                      {r.title}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}

